/* ----------------- React --------------- */
import { useState } from "react";

/* ----------------- Child components --------------- */
import AccountSettingsApplet from "@memorang/ui/components/AccountSettingsApplet";
import AppSelectionDialog from "../components/dialogs/AppSelectionDialog";
import DeleteConfirmationDialog from "../components/dialogs/DeleteConfirmationDialog";
import DeleteFeedbackDialog from "../components/dialogs/DeleteFeedbackDialog";
import { SignoutDialog } from "../components/dialogs/SignOutDialog";

import { useThemeContext } from "@contexts/ThemeContext";
/* ----------------- Hooks --------------- */
import useAccountSettings, {
	type DialogType,
} from "../hooks/useAccountSettings";
import { useDelete } from "../hooks/useDelete";

import FaqsDialog from "@features/faqs/components/FaqsDialog";
import { useManualRedeemCheck } from "@features/redeem/hooks/useManualRedeemCheck";
import { buildType } from "@helpers/app-identity";
import { useAppStore } from "@hooks/useAppStore";
import { useGlobalStore } from "@hooks/useGlobalStore";
import { isWeb } from "@memorang/helpers/platform";
import { FeedbackDialogContainer } from "../../../containers/FeedbackDialogContainer";
/* ----------------- UI Libraries --------------- */

import { pageViewTypes } from "@constants/tracking";
import { useExamsFilterDialog } from "@contexts/DialogContext/dialogs/exams-filter";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { AppBar } from "@features/dashboard/layouts/AppBar";
import LanguageDialog from "@features/settings/components/LanguageDialog";
import useAnalytics from "@hooks/useAnalytics";
import type { Apps } from "@memorang/constants/apps";
import ThemeDialog from "@memorang/ui/components/AccountSettingsApplet/dialogs/ThemeDialog";
import { useQueryClient } from "@tanstack/react-query";
import { getCachedExamsList } from "api/exams-query";
import SetExamDateContainer from "components/SetExamDateContainer";
import { router } from "expo-router";
import { useTranslation } from "react-i18next";

type Props = {
	showBackButton?: boolean;
};

const AccountSettings = ({ showBackButton }: Props) => {
	const [openDialog, setOpenDialog] = useState<DialogType | undefined>();
	const {
		toggleShowDeleteConfirmationDialog,
		toggleShowDeleteFeedbackDialog,
		showDeleteConfirmationDialog,
		showDeleteFeedbackDialog,
		selectedReasons,
		feedback,
		handleSetFeedback,
		handleSendFeedback,
		checkboxStates,
		handleChangeCheckboxState,
	} = useDelete();

	const queryClient = useQueryClient();
	const { userLogicalId } = useAuthContext();
	const exams = getCachedExamsList(queryClient, userLogicalId);
	const hasMoreThanOneExam = exams && exams.length > 1;
	const showAudioPlaybackTestDialog = useGlobalStore(
		(state) => state.showAudioPlaybackTestDialog,
	);
	const selectedApp = useGlobalStore((state) => state.selectedApp);

	const { showExamsFilter } = useExamsFilterDialog();
	const handleNavigateToExamsRoute = () => {
		showExamsFilter();
	};
	const { t } = useTranslation();
	useAnalytics({
		type: pageViewTypes.settings,
	});
	const { isDarkMode, toggleDarkMode, themePreference, setThemePreference } =
		useThemeContext();
	const appFont = useAppStore((store) => store.app.font);
	const toggleAudioPlaybackTestDialog = useGlobalStore(
		(state) => state.toggleAudioPlaybackTestDialog,
	);
	const adminPaywallEnabled = useGlobalStore(
		(state) => state.adminPaywallEnabled,
	);
	const setAdminPaywallEnabled = useGlobalStore(
		(state) => state.setAdminPaywallEnabled,
	);

	const { handleRedeemPurchase } = useManualRedeemCheck();

	const { getAccountSections } = useAccountSettings({
		toggleDialog: setOpenDialog,
		toggleShowDeleteFeedbackDialog,
		toggleAudioPlaybackTestDialog,
		onRedeemPurchase: handleRedeemPurchase,
	});

	const handleCloseDialog = () => setOpenDialog(undefined);

	const sections = getAccountSections();

	return (
		<>
			<AppBar
				title={t("account.title")}
				onPressBack={showBackButton ? () => router.back() : undefined}
				hideExamSwitcher
			/>
			<AccountSettingsApplet
				sections={sections}
				font={appFont}
				isDarkMode={isDarkMode}
				showAudioPlaybackTestDialog={showAudioPlaybackTestDialog}
				adminPaywallEnabled={adminPaywallEnabled}
				accountExtendValidity={t("account.extendValidity")}
				hasMoreThanOneExam={hasMoreThanOneExam}
				toggleDarkMode={toggleDarkMode}
				toggleAudioPlaybackTestDialog={toggleAudioPlaybackTestDialog}
				toggleAdminPaywall={() => setAdminPaywallEnabled(!adminPaywallEnabled)}
				handleNavigateToExamsRoute={handleNavigateToExamsRoute}
			/>
			<SetExamDateContainer
				showDatePicker={openDialog === "DATE_PICKER"}
				toggleShowDatePicker={handleCloseDialog}
			/>
			<SignoutDialog
				visible={openDialog === "SIGN_OUT"}
				handleClose={handleCloseDialog}
			/>
			<DeleteFeedbackDialog
				visible={showDeleteFeedbackDialog}
				handleDismiss={toggleShowDeleteFeedbackDialog}
				handleShowDeleteConfirmation={toggleShowDeleteConfirmationDialog}
				feedback={feedback}
				handleSetFeedback={handleSetFeedback}
				handleSelectOption={handleChangeCheckboxState}
				checkboxStates={checkboxStates}
				selectedReasons={selectedReasons}
			/>
			<DeleteConfirmationDialog
				visible={showDeleteConfirmationDialog}
				handleDismiss={toggleShowDeleteConfirmationDialog}
				handleSendFeedback={handleSendFeedback}
			/>
			<LanguageDialog
				visible={openDialog === "LANGUAGE"}
				handleClose={handleCloseDialog}
			/>
			<ThemeDialog
				visible={openDialog === "THEME"}
				preference={themePreference}
				onSelect={setThemePreference}
				onDismiss={handleCloseDialog}
			/>
			<FaqsDialog
				visible={openDialog === "FAQS"}
				handleClose={handleCloseDialog}
			/>
			<FeedbackDialogContainer
				visible={isWeb && openDialog === "FEEDBACK"}
				onDismiss={handleCloseDialog}
			/>
			<AppSelectionDialog
				visible={openDialog === "APP_SELECTION"}
				handleClose={handleCloseDialog}
				currentApp={selectedApp || (buildType as Apps)}
			/>
		</>
	);
};

export default AccountSettings;
